import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { hoyEcuador, inicioMesEcuador, haceDiasEcuador } from '../../utils/fechas';

const BASE = { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE };

const money = (v) => `$${(parseFloat(v) || 0).toFixed(2)}`;
const UNIT_LABELS = { unit: 'u', kg: 'kg', g: 'g', l: 'L', ml: 'ml', oz: 'oz', lb: 'lb' };
const formatQty = (v) => {
    const n = parseFloat(v) || 0;
    return Number.isInteger(n) ? n.toLocaleString('es-EC') : n.toLocaleString('es-EC', { maximumFractionDigits: 3 });
};
const formatDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('es-EC', { weekday: 'short', day: 'numeric', month: 'short' });
const formatDateTime = (v) => new Date(v).toLocaleString('es-EC', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' });

const card = { background: 'white', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '1.1rem', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' };
const th = { textAlign: 'left', padding: '9px 12px', fontSize: '12px', color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '9px 12px', fontSize: '14px', borderBottom: '1px solid #f1f5f9' };
const profitColor = (v) => (v >= 0 ? '#15803d' : '#dc2626');

const PRESETS = [
    { id: 'hoy', label: 'Hoy' },
    { id: 'ayer', label: 'Ayer' },
    { id: '7d', label: 'Últimos 7 días' },
    { id: 'mes', label: 'Este mes' },
    { id: 'rango', label: 'Rango' }
];

const rangeFor = (preset, from, to) => {
    switch (preset) {
        case 'ayer': return [haceDiasEcuador(1), haceDiasEcuador(1)];
        case '7d': return [haceDiasEcuador(6), hoyEcuador()];
        case 'mes': return [inicioMesEcuador(), hoyEcuador()];
        case 'rango': return [from || hoyEcuador(), to || hoyEcuador()];
        default: return [hoyEcuador(), hoyEcuador()];
    }
};

/**
 * Costos y Ganancias: lo que entró por ventas, lo que costaron los insumos vendidos,
 * lo que se gastó comprando insumos y en gastos de caja, y la ganancia resultante.
 */
const Ganancias = () => {
    const [preset, setPreset] = useState('hoy');
    const [customFrom, setCustomFrom] = useState(hoyEcuador());
    const [customTo, setCustomTo] = useState(hoyEcuador());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('productos');

    const [dateFrom, dateTo] = rangeFor(preset, customFrom, customTo);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/orders/profit-report/', { ...BASE, params: { date_from: dateFrom, date_to: dateTo } });
            setData(res.data);
        } catch (err) {
            console.error('Error cargando costos y ganancias:', err);
            alert('No se pudo cargar el reporte de costos y ganancias.');
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    const chip = (active) => ({
        padding: '7px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
        border: `1px solid ${active ? 'var(--primary-color, #4a1f0c)' : '#e5e7eb'}`,
        background: active ? 'var(--primary-color, #4a1f0c)' : 'white', color: active ? 'white' : '#475569'
    });
    const kpi = (label, value, color, sub, icon) => (
        <div style={{ ...card, padding: '0.9rem 1rem' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {icon && <i className={`bi ${icon}`}></i>} {label}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: '800', color }}>{value}</div>
            {sub && <div style={{ fontSize: '12px', color: '#64748b' }}>{sub}</div>}
        </div>
    );
    const tabButton = (id, label) => (
        <button type="button" onClick={() => setTab(id)} style={{
            padding: '8px 14px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '14px',
            background: tab === id ? '#1f2937' : 'transparent', color: tab === id ? 'white' : '#475569'
        }}>{label}</button>
    );

    const s = data?.summary;

    return (
        <div style={{ padding: '1rem', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Encabezado */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                <div style={{ ...card, padding: 0, width: '52px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: 'var(--primary-color, #4a1f0c)' }}>
                    <i className="bi bi-piggy-bank"></i>
                </div>
                <div>
                    <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#1f2937', margin: 0 }}>Costos y Ganancias</h1>
                    <p style={{ color: '#6b7280', margin: '2px 0 0', fontSize: '13px' }}>
                        Lo que entró por ventas, lo que costaron los insumos, lo que se gastó y lo que realmente se ganó.
                    </p>
                </div>
            </div>

            {/* Fechas */}
            <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <i className="bi bi-calendar3" style={{ color: '#64748b' }}></i>
                {PRESETS.map(p => (
                    <button key={p.id} type="button" onClick={() => setPreset(p.id)} style={chip(preset === p.id)}>{p.label}</button>
                ))}
                {preset === 'rango' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #d1d5db' }} />
                        <span style={{ color: '#94a3b8' }}>a</span>
                        <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #d1d5db' }} />
                    </div>
                )}
                {loading && <span style={{ fontSize: '13px', color: '#94a3b8' }}>Cargando...</span>}
            </div>

            {!data ? (
                <p style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>{loading ? 'Cargando reporte...' : 'Sin datos'}</p>
            ) : (
                <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                    {/* Resumen principal */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                        {kpi('Ventas (ingresos)', money(s.sales), 'var(--primary-color, #4a1f0c)', `${s.orders} venta(s), incluye fiados`, 'bi-cash-stack')}
                        {kpi('Costo de lo vendido', money(s.cost_of_sales), '#b45309', 'insumos que salieron por las ventas', 'bi-basket')}
                        {kpi('Ganancia bruta', money(s.gross_profit), profitColor(s.gross_profit), `margen ${s.gross_margin_pct}%`, 'bi-graph-up-arrow')}
                        {kpi('Gastos de caja', money(s.expenses), '#dc2626', s.losses > 0 ? `+ mermas de bodega ${money(s.losses)}` : 'registrados en Gastos', 'bi-wallet2')}
                        {kpi('Ganancia neta', money(s.net_profit), profitColor(s.net_profit), 'bruta − gastos − mermas', 'bi-trophy')}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                        {kpi('Gastado en compras de bodega', money(s.purchases), '#7c3aed', 'precio de compra de los insumos que ingresaron en el período', 'bi-cart-plus')}
                        {kpi('Valor del inventario (hoy)', money(s.inventory_value), '#0f766e', 'lo que hay en bodega, a precio de compra', 'bi-box-seam')}
                    </div>

                    {/* Avisos de datos incompletos */}
                    {(s.orders_without_cost > 0 || s.supplies_without_cost > 0) && (
                        <div style={{ ...card, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '13px', marginBottom: '14px' }}>
                            <i className="bi bi-exclamation-triangle-fill"></i>{' '}
                            {s.orders_without_cost > 0 && <>Hay <strong>{s.orders_without_cost} venta(s) por {money(s.sales_without_cost)}</strong> sin costo registrado (productos sin receta en Bodega o vendidos antes de cargar precios). </>}
                            {s.supplies_without_cost > 0 && <><strong>{s.supplies_without_cost} insumo(s)</strong> no tienen precio de compra. </>}
                            Completa las recetas y los precios en <strong>Bodega de Insumos</strong> para que la ganancia sea exacta.
                        </div>
                    )}

                    {/* Por línea de negocio */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                        {Object.entries(data.by_line).map(([key, l]) => (
                            <div key={key} style={card}>
                                <div style={{ fontWeight: '800', color: 'var(--primary-color, #4a1f0c)', marginBottom: '6px' }}>{l.label}</div>
                                <div style={{ fontSize: '13px', lineHeight: 1.8 }}>
                                    <div>Ventas: <strong>{money(l.sales)}</strong> <span style={{ color: '#94a3b8' }}>({l.orders})</span></div>
                                    <div>Costo: <strong style={{ color: '#b45309' }}>{money(l.cost)}</strong></div>
                                    <div>Ganancia: <strong style={{ color: profitColor(l.profit) }}>{money(l.profit)}</strong> <span style={{ color: '#94a3b8' }}>· {l.margin_pct}%</span></div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Detalle */}
                    <div style={card}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                            {tabButton('productos', 'Por producto')}
                            {tabButton('insumos', 'Insumos consumidos')}
                            {tabButton('compras', 'Compras de bodega')}
                            {tabButton('dias', 'Por día')}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            {tab === 'productos' && (
                                data.by_product.length === 0 ? <p style={{ color: '#64748b', textAlign: 'center' }}>No hay ventas en este período.</p> : (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '680px' }}>
                                        <thead><tr>
                                            <th style={th}>Producto</th><th style={th}>Cantidad</th><th style={th}>Ventas</th>
                                            <th style={th}>Costo</th><th style={th}>Ganancia</th><th style={th}>Margen</th>
                                        </tr></thead>
                                        <tbody>
                                            {data.by_product.map(p => (
                                                <tr key={p.product_id || p.name}>
                                                    <td style={{ ...td, fontWeight: 600 }}>
                                                        {p.name}
                                                        {!p.has_recipe && <span style={{ marginLeft: '6px', fontSize: '11px', padding: '1px 7px', borderRadius: '8px', background: '#fef3c7', color: '#b45309' }}>sin receta</span>}
                                                    </td>
                                                    <td style={td}>{formatQty(p.quantity)}</td>
                                                    <td style={td}>{money(p.sales)}</td>
                                                    <td style={{ ...td, color: '#b45309' }}>{money(p.cost)}</td>
                                                    <td style={{ ...td, fontWeight: 700, color: profitColor(p.profit) }}>{money(p.profit)}</td>
                                                    <td style={td}>{p.margin_pct}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}
                            {tab === 'insumos' && (
                                data.supplies_used.length === 0 ? <p style={{ color: '#64748b', textAlign: 'center' }}>No salieron insumos por ventas en este período.</p> : (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '480px' }}>
                                        <thead><tr><th style={th}>Insumo</th><th style={th}>Cantidad usada</th><th style={th}>Costo</th></tr></thead>
                                        <tbody>
                                            {data.supplies_used.map(u => (
                                                <tr key={u.supply_id}>
                                                    <td style={{ ...td, fontWeight: 600 }}>{u.name}</td>
                                                    <td style={td}>{formatQty(u.quantity)} {UNIT_LABELS[u.unit] || u.unit}</td>
                                                    <td style={{ ...td, color: '#b45309', fontWeight: 600 }}>{money(u.cost)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}
                            {tab === 'compras' && (
                                data.purchases.length === 0 ? <p style={{ color: '#64748b', textAlign: 'center' }}>No se registraron compras de insumos en este período.</p> : (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
                                        <thead><tr>
                                            <th style={th}>Fecha</th><th style={th}>Insumo</th><th style={th}>Cantidad</th>
                                            <th style={th}>Precio unitario</th><th style={th}>Total</th><th style={th}>Motivo</th>
                                        </tr></thead>
                                        <tbody>
                                            {data.purchases.map(c => (
                                                <tr key={c.id}>
                                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(c.created_at)}</td>
                                                    <td style={{ ...td, fontWeight: 600 }}>{c.supply}</td>
                                                    <td style={td}>{formatQty(c.quantity)} {c.unit}</td>
                                                    <td style={td}>{c.unit_cost > 0 ? `$${c.unit_cost.toFixed(c.unit_cost >= 1 ? 2 : 4)}` : <span style={{ color: '#b45309' }}>sin precio</span>}</td>
                                                    <td style={{ ...td, fontWeight: 700, color: '#7c3aed' }}>{money(c.total_cost)}</td>
                                                    <td style={{ ...td, fontSize: '13px', color: '#64748b' }}>{c.reason || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}
                            {tab === 'dias' && (
                                data.by_day.length === 0 ? <p style={{ color: '#64748b', textAlign: 'center' }}>Sin movimientos en este período.</p> : (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
                                        <thead><tr>
                                            <th style={th}>Día</th><th style={th}>Ventas</th><th style={th}>Costo vendido</th>
                                            <th style={th}>Ganancia bruta</th><th style={th}>Compras bodega</th><th style={th}>Gastos caja</th>
                                        </tr></thead>
                                        <tbody>
                                            {data.by_day.map(d => (
                                                <tr key={d.date}>
                                                    <td style={{ ...td, fontWeight: 600, textTransform: 'capitalize' }}>{formatDay(d.date)}</td>
                                                    <td style={td}>{money(d.sales)}</td>
                                                    <td style={{ ...td, color: '#b45309' }}>{money(d.cost)}</td>
                                                    <td style={{ ...td, fontWeight: 700, color: profitColor(d.gross_profit) }}>{money(d.gross_profit)}</td>
                                                    <td style={{ ...td, color: '#7c3aed' }}>{money(d.purchases)}</td>
                                                    <td style={{ ...td, color: '#dc2626' }}>{money(d.expenses)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}
                        </div>
                    </div>

                    <p style={{ fontSize: '12px', color: '#64748b', marginTop: '10px', lineHeight: 1.6 }}>
                        <strong>Cómo leerlo:</strong> el <em>costo de lo vendido</em> es lo que costaron (a precio de compra) los insumos de las recetas de cada venta;
                        así una lonchera suma su jugo, mandarina, sánduche, etc. Las <em>compras de bodega</em> son el dinero que salió para reponer insumos en esas fechas:
                        no se restan otra vez de la ganancia, porque ese costo se reconoce cuando el insumo se vende.
                    </p>
                </div>
            )}
        </div>
    );
};

export default Ganancias;
